/**
 * El aspecto del panel: oscuro, claro o el del sistema (Rais, 10-oct-2026: «white mode, black mode, tenerlo en
 * Ajustes»).
 *
 * Los colores del panel eran fijos y oscuros, escritos uno a uno en cada estilo. Ahora cada uno es una variable
 * (`var(--hi-…)`) con dos juegos de valores, y lo que se elige en Ajustes lo guarda el MOTOR, no el navegador:
 * el panel de Word y la pantalla grande son dos navegadores distintos y tienen que verse igual.
 */

export type Tema = "oscuro" | "claro" | "sistema";
export const TEMAS: readonly [Tema, string][] = [["oscuro", "Oscuro"], ["claro", "Claro"], ["sistema", "Sistema"]];
export const esTema = (x: unknown): x is Tema => x === "oscuro" || x === "claro" || x === "sistema";

const OSCURO = `
  --hi-fondo:#0c1117; --hi-papel:#11161f; --hi-papel-2:#16202c; --hi-raya:#1f2937; --hi-raya-2:#333344;
  --hi-tinta:#e8edf3; --hi-suave:#cbd5e1; --hi-tenue:#8a93a3; --hi-apagado:#6b7280;
  --hi-humano:#27B6E3; --hi-humano-claro:#7FD9F2; --hi-oficio:#FFC400; --hi-sobre-acento:#0c1117;
  --hi-mal:#ee6666; --hi-mal-suave:#fca5a5; --hi-mal-fondo:#3a1414; --hi-mal-raya:#aa3333; --hi-rojo:#ef4444;
  --hi-bien:#33aa77; --hi-bien-suave:#86efac; --hi-bien-fondo:#0f2a1a; --hi-bien-raya:#22aa77; --hi-verde:#22c55e;
  --hi-aviso:#fbbf24; --hi-aviso-suave:#e8c874; --hi-aviso-fondo:#3a2a10; --hi-aviso-raya:#7a5a20; --hi-ambar:#f59e0b;
  --hi-info:#cfe0ff; --hi-info-2:#8fb3ff; --hi-info-fondo:#14243f; --hi-info-raya:#24406e;
  --hi-menu:#141b26; --hi-menu-activo:#27B6E3; --hi-sombra:rgba(0,0,0,.5); --hi-velo:rgba(4,7,11,.72);`;
const CLARO = `
  --hi-fondo:#f6f4ef; --hi-papel:#fffdf8; --hi-papel-2:#efeae0; --hi-raya:#e2ddd2; --hi-raya-2:#cfc9bb;
  --hi-tinta:#1c1a17; --hi-suave:#3a3732; --hi-tenue:#6b665e; --hi-apagado:#857f75;
  --hi-humano:#0b7fa8; --hi-humano-claro:#0b6f93; --hi-oficio:#8a6100; --hi-sobre-acento:#ffffff;
  --hi-mal:#b3261e; --hi-mal-suave:#8c1d18; --hi-mal-fondo:#fbe9e7; --hi-mal-raya:#e0a39d; --hi-rojo:#d9463b;
  --hi-bien:#1d7a45; --hi-bien-suave:#17663a; --hi-bien-fondo:#e6f4ea; --hi-bien-raya:#9fd3b0; --hi-verde:#1d8a4c;
  --hi-aviso:#8a5a00; --hi-aviso-suave:#7a5200; --hi-aviso-fondo:#fdf3d7; --hi-aviso-raya:#e3c77a; --hi-ambar:#b7791f;
  --hi-info:#1d3f7a; --hi-info-2:#2f5aa8; --hi-info-fondo:#e8f0fe; --hi-info-raya:#b9cdf0;
  --hi-menu:#ebe6da; --hi-menu-activo:#0b7fa8; --hi-sombra:rgba(0,0,0,.18); --hi-velo:rgba(60,55,45,.45);`;

/**
 * La hoja de la paleta del panel. El panel nació oscuro y, mientras el autor no elija otra cosa, sigue oscuro:
 * una actualización no le cambia el panel de color porque su sistema esté en claro. Con `data-hi-tema` en la
 * raíz manda lo elegido; «sistema» es la única opción que mira al sistema.
 */
export const CSS_DEL_TEMA = `
:root {${OSCURO}
}
:root[data-hi-tema="claro"] {${CLARO}
}
@media (prefers-color-scheme: light) { :root[data-hi-tema="sistema"] {${CLARO}
} }
html, body { background: var(--hi-fondo); }
`;

/**
 * Pone el tema en la página: `data-hi-tema` para el panel y `data-theme` para la pantalla grande, que usa
 * las palabras de siempre y, sin él, mira al sistema.
 */
export function aplicarTema(tema: Tema, raiz: HTMLElement = document.documentElement): void {
  raiz.dataset.hiTema = tema;
  if (tema === "sistema") delete raiz.dataset.theme;
  else raiz.dataset.theme = tema === "claro" ? "light" : "dark";
}

/**
 * Lo que el autor eligió, o `null` si todavía no ha elegido nada (o el motor no contesta): entonces cada
 * sitio sigue como estaba —el panel, oscuro; la pantalla grande, como el sistema—.
 */
export async function leerTema(base = ""): Promise<Tema | null> {
  try {
    const d = await (await fetch(`${base}/aspecto`)).json();
    return esTema(d?.tema) ? d.tema : null;
  } catch { return null; }
}

export async function guardarTema(tema: Tema, base = ""): Promise<boolean> {
  try {
    const r = await fetch(`${base}/aspecto`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ tema }) });
    return r.ok;
  } catch { return false; }
}
